import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check for stored credentials on mount
    const storedAuth = localStorage.getItem('mona_admin_auth');
    if (storedAuth) {
      try {
        const { username, password } = JSON.parse(storedAuth);
        login(username, password).catch(() => {
          localStorage.removeItem('mona_admin_auth');
        });
      } catch {
        localStorage.removeItem('mona_admin_auth');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (username, password) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Authorization': 'Basic ' + btoa(`${username}:${password}`),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        let errorMessage = 'Login failed';
        try {
          const data = await response.json();
          errorMessage = data.detail || errorMessage;
        } catch {
          errorMessage = `Server error (${response.status})`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Store credentials for subsequent requests
      const credentials = { username, password };
      localStorage.setItem('mona_admin_auth', JSON.stringify(credentials));
      
      setUser({
        ...data.user,
        credentials: btoa(`${username}:${password}`)
      });
      
      return data.user;
    } catch (err) {
      // Provide more helpful error messages
      let errorMessage = err.message;
      if (err.message === 'Failed to fetch' || err.message === 'Load failed') {
        errorMessage = 'Cannot connect to server. Please check your internet connection or try again later.';
      }
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('mona_admin_auth');
    setUser(null);
  };

  const getAuthHeader = () => {
    if (!user?.credentials) return {};
    return {
      'Authorization': `Basic ${user.credentials}`
    };
  };

  const isSuperAdmin = () => {
    return user?.role === 'super_admin';
  };

  const isAdmin = () => {
    return user?.role === 'admin' || user?.role === 'super_admin';
  };

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      error,
      login,
      logout,
      getAuthHeader,
      isSuperAdmin,
      isAdmin,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};
